import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Copy, Share2, Loader2, X, Trash2, Check, Lock } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * ShareDialog — creates + surfaces a private share link for a card or crate.
 *
 * Props:
 *   * open              boolean
 *   * onClose           () => void
 *   * kind              "card" | "crate"
 *   * target            { id?, title, subtitle, image } — used pre-fetch and
 *                       for the native Web Share API text.
 *   * payload           { card_id } or { bucket }  — passed to POST /share
 *
 * We create the share lazily *on mount when opened*, and let the user copy,
 * open the native share sheet, or revoke. The share token is URL-safe and
 * always requires login to view — recipients see a login wall first.
 */
export default function ShareDialog({ open, onClose, kind, target, payload }) {
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(null); // {token, url, ...}
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const createShare = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/share', payload);
      setShare(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create share');
      onClose?.();
    } finally {
      setBusy(false);
    }
  }, [payload, onClose]);

  useEffect(() => {
    if (!open) {
      // Reset on close so re-open gives a fresh state.
      setShare(null);
      setCopied(false);
      return;
    }
    createShare();
  }, [open, createShare]);

  const shareText = share
    ? kind === 'card'
      ? `Check out "${target?.title}" by ${target?.subtitle || 'artist'} on GroovLens \u2014 ${share.url}`
      : `Peek at my ${target?.title || 'GroovLens'} crate \u2014 ${share.url}`
    : '';

  const handleCopy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed \u2014 select and copy the link manually');
    }
  };

  const handleNativeShare = async () => {
    if (!share) return;
    if (!navigator.share) {
      // Fall back to copy on desktop browsers without Web Share.
      handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: kind === 'card'
          ? `${target?.title || 'Track'} \u00b7 GroovLens`
          : `${target?.title || 'Crate'} \u00b7 GroovLens`,
        text: shareText,
        url: share.url,
      });
    } catch (e) {
      // User cancelled OR share threw. Only surface real errors.
      if (e?.name !== 'AbortError') {
        toast.error('Share failed');
      }
    }
  };

  const handleRevoke = async () => {
    if (!share) return;
    if (!window.confirm('Revoke this share link? Anyone with the link will lose access.')) return;
    setRevoking(true);
    try {
      await api.delete(`/share/${share.token}`);
      toast.success('Share revoked');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-md w-[95vw] rounded-2xl"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[hsl(var(--primary))]" />
            Share {kind === 'card' ? 'track' : 'crate'}
          </DialogTitle>
          <DialogDescription>
            Only signed-in GroovLens users can open this link.
          </DialogDescription>
        </DialogHeader>

        {/* Preview */}
        <div className="rounded-xl border border-border bg-secondary/60 p-3 flex items-center gap-3">
          <div className="w-14 h-14 rounded-lg overflow-hidden bg-secondary grid place-items-center shrink-0">
            {target?.image ? (
              <img src={target.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <Share2 className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{target?.title || 'Untitled'}</div>
            {target?.subtitle && (
              <div className="text-xs text-muted-foreground truncate">{target.subtitle}</div>
            )}
            <div className="flex items-center gap-1.5 mt-1">
              <Lock className="w-3 h-3 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">Login required</span>
              {share?.view_count > 0 && (
                <Badge variant="outline" className="text-[9px] py-0 px-1.5 ml-1">
                  {share.view_count} view{share.view_count === 1 ? '' : 's'}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        {busy || !share ? (
          <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Preparing link…</span>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <Input
                readOnly
                value={share.url}
                onFocus={(e) => e.target.select()}
                className="rounded-lg font-mono text-xs"
                data-testid="share-dialog-url"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                className="rounded-lg shrink-0"
                data-testid="share-dialog-copy"
                aria-label="Copy link"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                onClick={handleNativeShare}
                className="rounded-lg"
                data-testid="share-dialog-native"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share via…
              </Button>
              <Button
                variant="outline"
                onClick={handleRevoke}
                disabled={revoking}
                className="rounded-lg text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))]"
                data-testid="share-dialog-revoke"
              >
                {revoking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Revoke link
              </Button>
            </div>
          </>
        )}

        <div className="flex justify-end pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-lg"
            data-testid="share-dialog-close"
          >
            <X className="w-4 h-4 mr-1.5" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
